import type { Vec2 } from "../types";
import { cloneVec, length, sub } from "./math";

interface MotionSample {
  elapsed: number;
  position: Vec2;
  progress: number;
  wallContact: boolean;
  speedSign: number;
}

export class RecoveryTracker {
  readonly fixedDt: number;
  readonly observationWindowSeconds = 1.5;
  readonly lowDisplacementDistance = 10;
  readonly lowForwardProgress = 4;
  readonly contactFraction = 0.12;
  readonly oscillationDirectionChanges = 3;
  readonly recoveryForwardProgress = 24;
  readonly recoveryClearSeconds = 0.65;
  readonly stuckTerminationSeconds = 8;
  readonly noProgressTerminationSeconds = 12;
  samples: MotionSample[] = [];
  stuck = false;
  stuckSince: number | null = null;
  stuckProgress = 0;
  clearSeconds = 0;
  stuckEvents = 0;
  recoveredEvents = 0;
  recoveryTimeTotal = 0;
  lastRecoveryTime: number | null = null;
  reverseActivations = 0;
  reverseTime = 0;
  reverseDistance = 0;
  successfulRecoveriesAfterReverse = 0;
  unsuccessfulReverseLoops = 0;
  private reverseActive = false;
  private reverseActivationsAtStuck = 0;
  private lastPosition: Vec2 | null = null;
  private lastMeaningfulProgressAt = 0;
  private meaningfulProgressReference = 0;
  private stuckUsedReverse = false;

  constructor(fixedDt: number) {
    this.fixedDt = fixedDt;
  }

  reanchor(elapsed: number, position: Vec2, totalProgress: number): void {
    this.samples = [];
    this.stuck = false;
    this.stuckSince = null;
    this.stuckProgress = totalProgress;
    this.clearSeconds = 0;
    this.reverseActive = false;
    this.lastPosition = cloneVec(position);
    this.lastMeaningfulProgressAt = elapsed;
    this.meaningfulProgressReference = totalProgress;
    this.stuckUsedReverse = false;
    this.reverseActivationsAtStuck = this.reverseActivations;
  }

  update(elapsed: number, position: Vec2, totalProgress: number, wallContact: boolean, speed: number): void {
    const reverseActive = speed < -0.5;
    if (reverseActive && !this.reverseActive) {
      this.reverseActivations += 1;
      if (this.stuck) this.stuckUsedReverse = true;
    }
    if (reverseActive) {
      this.reverseTime += this.fixedDt;
      if (this.lastPosition) this.reverseDistance += length(sub(position, this.lastPosition));
    }
    this.reverseActive = reverseActive;
    this.lastPosition = cloneVec(position);
    if (totalProgress - this.meaningfulProgressReference >= this.lowForwardProgress) {
      this.lastMeaningfulProgressAt = elapsed;
      this.meaningfulProgressReference = totalProgress;
    }
    const speedSign = speed > 0.5 ? 1 : speed < -0.5 ? -1 : 0;
    this.samples.push({ elapsed, position: cloneVec(position), progress: totalProgress, wallContact, speedSign });
    const oldestAllowed = elapsed - this.observationWindowSeconds;
    while (this.samples.length > 1 && (this.samples[1]?.elapsed ?? Infinity) <= oldestAllowed) this.samples.shift();
    if (this.stuck) this.updateRecovery(elapsed, totalProgress, wallContact);
    else if (this.isStuckWindow(elapsed)) {
      this.stuck = true;
      this.stuckSince = elapsed;
      this.stuckProgress = totalProgress;
      this.clearSeconds = 0;
      this.stuckEvents += 1;
      this.stuckUsedReverse = reverseActive;
      this.reverseActivationsAtStuck = this.reverseActivations;
    }
  }

  private isStuckWindow(elapsed: number): boolean {
    const first = this.samples[0];
    const last = this.samples.at(-1);
    if (!first || !last || last.elapsed - first.elapsed < this.observationWindowSeconds * 0.92) return false;
    const displacement = length(sub(last.position, first.position));
    const forwardProgress = Math.max(0, last.progress - first.progress);
    const contactRatio = this.samples.filter((item) => item.wallContact).length / this.samples.length;
    const signs = this.samples.map((item) => item.speedSign).filter(Boolean);
    let changes = 0;
    for (let index = 1; index < signs.length; index += 1) if (signs[index] !== signs[index - 1]) changes += 1;
    const timedOut = elapsed - this.lastMeaningfulProgressAt >= this.observationWindowSeconds * 2;
    const context = contactRatio >= this.contactFraction || changes >= this.oscillationDirectionChanges || timedOut;
    return displacement <= this.lowDisplacementDistance && forwardProgress <= this.lowForwardProgress && context;
  }

  private updateRecovery(elapsed: number, totalProgress: number, wallContact: boolean): void {
    const madeProgress = totalProgress - this.stuckProgress >= this.recoveryForwardProgress;
    this.clearSeconds = madeProgress && !wallContact ? this.clearSeconds + this.fixedDt : 0;
    if (this.clearSeconds < this.recoveryClearSeconds || this.stuckSince === null) return;
    const recoveryTime = elapsed - this.stuckSince;
    this.recoveredEvents += 1;
    this.recoveryTimeTotal += recoveryTime;
    this.lastRecoveryTime = recoveryTime;
    if (this.stuckUsedReverse || this.reverseActivations > this.reverseActivationsAtStuck) this.successfulRecoveriesAfterReverse += 1;
    this.stuck = false;
    this.stuckSince = null;
    this.clearSeconds = 0;
  }

  get stuckSeconds(): number {
    const elapsed = this.samples.at(-1)?.elapsed;
    return this.stuck && this.stuckSince !== null && elapsed !== undefined ? Math.max(0, elapsed - this.stuckSince) : 0;
  }

  get shouldTerminate(): boolean {
    const elapsed = this.samples.at(-1)?.elapsed;
    if (elapsed === undefined) return false;
    const stuckTimeout = this.stuck && this.stuckSeconds >= this.stuckTerminationSeconds;
    const noProgressTimeout = elapsed - this.lastMeaningfulProgressAt >= this.noProgressTerminationSeconds;
    if ((stuckTimeout || noProgressTimeout) && this.reverseActivations > 1) {
      this.unsuccessfulReverseLoops = Math.max(this.unsuccessfulReverseLoops, this.reverseActivations - this.successfulRecoveriesAfterReverse);
    }
    return stuckTimeout || noProgressTimeout;
  }
}
